import { Card, Typography } from '@achieve/ascend'
import { ArrowLeft, X } from 'react-feather'
import { useEffect, useState } from 'react'
import { useViewportSmallerThan } from 'utils/viewport'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { BREAKPOINTS } from 'constants/viewport'
import styles from './QuizFlowQuestion.module.scss'
import { GradientBadge } from './GradientBadge'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'

import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import { createExperimentObject } from 'utils/experiments'

const QuizFlowQuestion = function ({ quizQuestions, onComplete, onClose }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [selectedAnswers, setSelectedAnswers] = useState([])

  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)

  // for fire and forget events
  const { fireAndForget } = useTriggerEvents()
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const PFQBannerExperiment = useFeatureFlag(
    FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER
  )

  useEffect(() => {
    // Disable scrolling when the card is displayed
    document.body.style.overflow = 'hidden'

    // Question Loaded/Displaying event
    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: 'PFQ Questions',
      track_event: 'product_fit_quiz_questions_loaded',
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    // Re-enable scrolling when the component is unmounted
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [PFQBannerExperiment, fireAndForget, siteEnv])

  const handleAnswerSelect = (answerIndex) => {
    // Record the current answer
    const updatedAnswers = [...selectedAnswers]
    updatedAnswers[currentQuestionIndex] = answerIndex
    setSelectedAnswers(updatedAnswers)

    // If this is the last question, complete the quiz
    if (currentQuestionIndex >= quizQuestions.length - 1) {
      onComplete({ answers: updatedAnswers })
      return
    }

    // Handle navigation to next question
    const currentQuestion = quizQuestions[currentQuestionIndex]
    const nextQuestionMap = currentQuestion?.nextQuestionOverride
    const nextQuestionIndex = nextQuestionMap?.[answerIndex]

    // If we have a valid override, use it
    if (nextQuestionIndex !== undefined) {
      // Fill skipped questions with placeholder (-1)
      const skippedQuestionCount = nextQuestionIndex - currentQuestionIndex - 1
      if (skippedQuestionCount > 0) {
        for (let i = 0; i < skippedQuestionCount; i++) {
          updatedAnswers.push(-1)
        }
        setSelectedAnswers(updatedAnswers)
      }
      setCurrentQuestionIndex(nextQuestionIndex)
    } else {
      // Default: go to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      // Find the previous non-skipped question
      let newIndex = currentQuestionIndex - 1
      while (newIndex >= 0 && selectedAnswers[newIndex] === -1) {
        newIndex--
      }

      // Create a copy of the selectedAnswers array
      const updatedAnswers = [...selectedAnswers]

      // Clear the selection for the question we're going back to
      updatedAnswers[newIndex] = undefined

      // Remove answers for this question and any skipped questions
      // This ensures that if the user changes their previous answer,
      // the quiz flow can take a different path
      updatedAnswers.length = newIndex + 1

      setSelectedAnswers(updatedAnswers)
      setCurrentQuestionIndex(newIndex)
    }
  }

  const progressPercentage = ((currentQuestionIndex + 1) / quizQuestions.length) * 100

  return (
    <div className={styles['question-container']}>
      <Card className={styles['question-card']} variant="elevation">
        <div className={styles['clickable-container']}>
          <div style={{ height: '24px', width: '24px' }}>
            {currentQuestionIndex > 0 && (
              <div
                onClick={handleBack}
                style={{
                  cursor: 'pointer',
                  minWidth: '25px',
                  minHeight: '24px',
                  marginLeft: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                data-testid="pfq-back-icon"
              >
                <ArrowLeft color="black" width={21} height={20} />
              </div>
            )}
          </div>
          <TrackingWrapper
            track={{
              event_type: 'ui_click',
              event_action: 'ui_click',
              nav_link_section: 'PFQ Questions',
              track_event: 'product_fit_quiz_questions_close',
              pfq_state: {
                quizQuestions,
                currentQuestionIndex,
                selectedAnswers,
              },
            }}
            onClick={onClose}
          >
            <div
              className="cursor-pointer"
              style={{
                minWidth: '24px',
                minHeight: '24px',
                marginLeft: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <X
                size={20}
                color={'black'}
                priority={false}
                aria-hidden={true}
                alt="Close button"
                data-testid="pfq-x-icon"
              />
            </div>
          </TrackingWrapper>
        </div>
        <div className={styles['content-container']}>
          {/* Inline styles remain here bc theres 'animation' */}
          <div
            style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#E7ECF3',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                backgroundColor: '#3300FF',
                transition: 'width 0.3s ease',
                borderRadius: '8px',
              }}
            />
          </div>
          <div className={styles['badge-container']}>
            <GradientBadge width={44} height={33}>
              <Typography variant="labelButtonSm" color={'#3300FF'}>
                {`${currentQuestionIndex + 1}/${quizQuestions.length}`}
              </Typography>
            </GradientBadge>
          </div>
          <Typography
            variant={isMobile ? 'headingSm' : 'headingMd'}
            className={styles['question-heading']}
          >
            {quizQuestions[currentQuestionIndex].question}
          </Typography>
          <div className={styles['options-container']}>
            {quizQuestions[currentQuestionIndex].options.map((option, index) => (
              <div
                key={index}
                style={{
                  marginBottom:
                    index < quizQuestions[currentQuestionIndex].options.length - 1 ? '12px' : '0',
                }}
              >
                <label>
                  <input
                    type="radio"
                    name="quizOption"
                    value={option}
                    checked={selectedAnswers[currentQuestionIndex] === index}
                    onChange={() => handleAnswerSelect(index)}
                    style={{ marginRight: '10px' }}
                  />
                  <Typography variant={isMobile ? 'bodyMd' : 'bodyLg'}>{option}</Typography>
                </label>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  )
}

export { QuizFlowQuestion }
